import type { ReactNode } from 'react';

import { Chip } from '@/components/Chip/Chip';
import { Icon } from '@/components/Icon/Icon';
import type { LenderSummary } from '@/graphql/lenders';
import { countryFlag, countryName, groupByCountry, initialsOf } from '@/lib/lenders';

import styles from './LenderList.module.css';

export interface LenderListProps {
  lenders: readonly LenderSummary[];
  /** Wraps a row in a link to the lender; routing is the caller's. */
  renderLink: (lender: LenderSummary, children: ReactNode) => ReactNode;
}

function documentSummary(lender: LenderSummary): string {
  const live = lender.documents.filter((document) => document.status !== 'ARCHIVED').length;
  const archived = lender.documents.length - live;
  const parts = [live === 1 ? '1 document' : `${live} documents`];
  if (archived > 0) {
    parts.push(`${archived} archived`);
  }
  return parts.join(' · ');
}

/** Every lender, one section per country, each row opening the lender. */
export function LenderList({ lenders, renderLink }: LenderListProps) {
  return (
    <div className={styles.groups}>
      {groupByCountry(lenders).map(({ country, lenders: rows }) => {
        const heading = `${countryName(country)}`;
        return (
          <section aria-label={heading} className={styles.group} key={country ?? 'none'}>
            <h2 className={styles.heading}>
              <span aria-hidden="true" className={styles.flag}>
                {countryFlag(country)}
              </span>
              {heading}
              <span className={styles.count}>{rows.length}</span>
            </h2>
            <ul className={styles.list}>
              {rows.map((lender) => {
                const inactive = lender.status === 'INACTIVE';
                return (
                  <li key={lender.id}>
                    {renderLink(
                      lender,
                      <span className={`${styles.row} ${inactive ? styles.inactive : ''}`}>
                        <span aria-hidden="true" className={styles.monogram}>
                          {initialsOf(lender.name)}
                        </span>
                        <span className={styles.main}>
                          <span className={styles.name}>{lender.name ?? lender.key}</span>
                          <span className={styles.meta}>{documentSummary(lender)}</span>
                        </span>
                        <Chip tone={inactive ? 'warn' : 'good'}>
                          {inactive ? 'Inactive' : 'Active'}
                        </Chip>
                        <span className={styles.chevron}>
                          <Icon name="caret-right" />
                        </span>
                      </span>,
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
